/**
 * Automatic error reporter — sends errors to Corp DB for centralized monitoring.
 * Reports: React crashes, API errors, silent failures, performance issues.
 * Deduplicates via fingerprinting on the server side.
 */

import { getAccessToken } from './auth-token'

// Only activate when Corp API is explicitly configured — localhost:3010 fallback
// was causing 403 errors on remote LAN dev clients (#816).
// Defensive trim: a past Vercel misconfig stored the URL repeated 5× separated by
// newlines, which produced ERR_NAME_NOT_RESOLVED on every fetch (audit A1-#1 2026-05-28).
const CORP_REPORT_URL = (process.env.NEXT_PUBLIC_CORP_API_URL || '').split(/[\s,]+/).find(Boolean)?.replace(/\/+$/, '') || ''

type Severity = 'critical' | 'error' | 'warning' | 'info'
type Category = 'api_error' | 'ui_crash' | 'silent_failure' | 'performance' | 'auth' | 'sync' | 'network' | 'validation' | 'database'

interface ErrorReport {
  severity: Severity
  category: Category
  message: string
  errorCode?: string
  stackTrace?: string
  url?: string
  context?: Record<string, any>
}

// ── Queue & batching ────────────────────────────────────────────────────────
const MAX_QUEUE = 50
let queue: ErrorReport[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null

function ensureTimer() {
  if (flushTimer || typeof window === 'undefined') return
  flushTimer = setInterval(flush, 5_000)
}

// Pull tenant context from the in-memory access token (QA WEB-003, 2026-09-18
// — it's never in localStorage any more) so reports are attributable to a
// specific subscriber / database / tenant. Public endpoint — best-effort
// only; the backend also decodes the Authorization header as a fallback.
function readTenantContext(): { token: string | null; subscriberId?: string; databaseId?: string; tenantId?: string; userId?: string } {
  if (typeof window === 'undefined') return { token: null }
  const token = getAccessToken()
  if (!token) return { token: null }
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return { token }
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json)
    return {
      token,
      subscriberId: claims.sub_id,
      databaseId:   claims.dbi,
      tenantId:     claims.tid,
      userId:       claims.sub,
    }
  } catch {
    return { token }
  }
}

function flush() {
  if (queue.length === 0 || !CORP_REPORT_URL) return
  const batch = queue.splice(0)
  try {
    const ctx = readTenantContext()
    const payload = {
      appSource: 'web' as const,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION || process.env.NEXT_PUBLIC_BUILD_ID || 'dev',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'ssr',
      url: typeof window !== 'undefined' ? window.location.href : '',
      subscriberId: ctx.subscriberId,
      databaseId:   ctx.databaseId,
      tenantId:     ctx.tenantId,
      userId:       ctx.userId,
      errors: batch,
    }
    fetch(`${CORP_REPORT_URL}/api/corp/issues/report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(ctx.token ? { Authorization: `Bearer ${ctx.token}` } : {}),
      },
      body: JSON.stringify(payload),
    }).catch(() => {}) // swallow — never break the app
  } catch {
    // swallow
  }
}

function enqueue(report: ErrorReport) {
  if (queue.length >= MAX_QUEUE) queue.shift() // drop oldest
  queue.push(report)
  // Flush immediately for crashes — don't wait 5s
  if (report.severity === 'critical' || report.severity === 'error') {
    flush()
  } else {
    ensureTimer()
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

export function reportError(report: ErrorReport) {
  try { enqueue(report) } catch { /* never throw */ }
}

export function reportApiError(url: string, status: number, body: any) {
  try {
    const severity: Severity = status >= 500 ? 'error' : status === 401 || status === 403 ? 'warning' : 'info'
    enqueue({
      severity,
      category: 'api_error',
      message: `API ${status} — ${url}`,
      errorCode: String(status),
      url,
      context: { responseBody: typeof body === 'string' ? body.slice(0, 500) : body }
    })
  } catch { /* never throw */ }
}

export function reportSilentFailure(component: string, message: string, context?: Record<string, any>) {
  try {
    enqueue({ severity: 'warning', category: 'silent_failure', message: `[${component}] ${message}`, context })
  } catch { /* never throw */ }
}

export function reportPerformance(metric: string, value: number, threshold: number) {
  try {
    if (value <= threshold) return
    enqueue({
      severity: 'warning',
      category: 'performance',
      message: `${metric}: ${value}ms (threshold ${threshold}ms)`,
      context: { metric, value, threshold }
    })
  } catch { /* never throw */ }
}

export function setupGlobalErrorHandlers() {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (e) => {
    try {
      enqueue({
        severity: 'error',
        category: 'ui_crash',
        message: e.message || 'Unhandled error',
        stackTrace: e.error?.stack?.slice(0, 2000),
        url: e.filename
      })
    } catch { /* never throw */ }
  })
  window.addEventListener('unhandledrejection', (e) => {
    try {
      const err = e.reason
      enqueue({
        severity: 'error',
        category: 'ui_crash',
        message: err?.message || String(err) || 'Unhandled promise rejection',
        stackTrace: err?.stack?.slice(0, 2000)
      })
    } catch { /* never throw */ }
  })
}
